import { useMemo } from 'react';

import { type Service, serviceCatalogue, servicePillars } from '@/content/services';

/** One service with the stage it belongs to resolved to a name. */
export type ComparisonRow = {
  service: Service;
  /** The pillar's title, which is the only thing the row shows of it. */
  stage: string;
};

/**
 * Every service as one flat, comparable row.
 *
 * Flat on purpose, where `useServiceCatalogue` groups: the catalogue band is
 * making an argument about the order of an import and this band is answering
 * "which of these do I need", and a comparison broken into three tables is
 * three comparisons. The stage survives as a column instead, so the grouping
 * is still legible without being structural.
 *
 * `serviceCatalogue` is already in pillar order, so the rows arrive grouped
 * without being nested — find, then ship, then pay, top to bottom.
 *
 * The pillar lookup cannot miss: `pillar` is typed as `ServicePillarId`. The
 * fallback is for the compiler, as in `useServiceDetail`.
 */
export function useServiceComparison(): readonly ComparisonRow[] {
  return useMemo(
    () =>
      serviceCatalogue.map<ComparisonRow>((service) => ({
        service,
        stage: (servicePillars.find((p) => p.id === service.pillar) ?? servicePillars[0]).title,
      })),
    [],
  );
}
